import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps, type KeyboardEvent, type MouseEvent } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  BookOpen,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  MessageSquare,
  PanelLeft,
  PanelLeftClose,
  Plus,
  RefreshCw,
  Send,
  Sparkles,
  Square,
  Trash2,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import {
  getWikiSyncStatus,
  streamWikiChat,
  syncWiki,
  wikiAssistantKeys,
} from '../api';
import type { WikiAsset, WikiChatMessage, WikiSource, WikiSyncStatus, WikiThread } from '../types';

const THREADS_KEY = 'atek-wiki-assistant-threads';
const STARTER_PROMPTS = [
  'Update me on PHSA',
  'How does ATEK ingest LoRaWAN data?',
  'Explain gap recovery.',
  'What should I know about TS60x devices?',
  'What happened at Lallemand?',
  'How do the monorepo, edge stack, and n8n fit together?',
];

function generateId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function loadThreads(): WikiThread[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(THREADS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveThreads(threads: WikiThread[]) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(THREADS_KEY, JSON.stringify(threads));
}

function truncateTitle(text: string) {
  const compact = text.replace(/\s+/g, ' ').trim();
  return compact.length > 48 ? `${compact.slice(0, 45)}...` : compact || 'New conversation';
}

function groupThreadsByDate(threads: WikiThread[]) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86_400_000;
  const week = today - 7 * 86_400_000;
  const groups: Record<string, WikiThread[]> = {
    Today: [],
    Yesterday: [],
    'Previous 7 days': [],
    Older: [],
  };

  for (const thread of threads) {
    if (thread.updatedAt >= today) groups.Today.push(thread);
    else if (thread.updatedAt >= yesterday) groups.Yesterday.push(thread);
    else if (thread.updatedAt >= week) groups['Previous 7 days'].push(thread);
    else groups.Older.push(thread);
  }

  return Object.entries(groups)
    .filter(([, list]) => list.length > 0)
    .map(([label, list]) => ({ label, threads: list }));
}

function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <button
      type="button"
      onClick={copy}
      className={cn('inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700', className)}
      title="Copy"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function CodeBlock({ children, className }: ComponentProps<'code'> & { className?: string }) {
  const match = /language-(\w+)/.exec(className || '');
  const lang = match?.[1];
  const code = String(children).replace(/\n$/, '');

  if (!className) {
    return <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-900">{children}</code>;
  }

  return (
    <div className="my-3 overflow-hidden rounded-md border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-1.5">
        <span className="font-mono text-xs text-slate-500">{lang || 'code'}</span>
        <CopyButton text={code} />
      </div>
      <pre className="overflow-x-auto bg-slate-950 p-3">
        <code className="font-mono text-xs text-amber-200">{code}</code>
      </pre>
    </div>
  );
}

function collectSourceAssets(sources?: WikiSource[], limit = 8): WikiAsset[] {
  if (!sources?.length) return [];
  const assets = new Map<string, WikiAsset>();

  for (const source of sources) {
    for (const asset of source.assets ?? []) {
      if (!assets.has(asset.assetPath)) assets.set(asset.assetPath, asset);
      if (assets.size >= limit) return Array.from(assets.values());
    }
  }

  return Array.from(assets.values());
}

function WikiImageGrid({ assets, compact = false }: { assets: WikiAsset[]; compact?: boolean }) {
  if (assets.length === 0) return null;

  return (
    <div className={cn('mt-3 grid gap-2', compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2')}>
      {assets.map((asset) => (
        <a
          key={`${asset.assetPath}:${asset.alt}`}
          href={asset.githubUrl || asset.url}
          target="_blank"
          rel="noreferrer"
          className="group overflow-hidden rounded-md border border-slate-200 bg-slate-50 text-left transition-colors hover:border-slate-300"
        >
          <div className={cn('overflow-hidden bg-white', compact ? 'aspect-[4/3]' : 'aspect-video')}>
            <img
              src={asset.url}
              alt={asset.alt}
              loading="lazy"
              className="h-full w-full object-contain transition-transform group-hover:scale-[1.02]"
            />
          </div>
          <div className="truncate px-2 py-1.5 text-xs text-slate-600">{asset.alt}</div>
        </a>
      ))}
    </div>
  );
}

function MessageBubble({ message }: { message: WikiChatMessage }) {
  const isUser = message.role === 'user';
  const assets = !isUser ? collectSourceAssets(message.sources) : [];

  return (
    <div className={cn('group flex gap-3', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white">
          <BookOpen className="h-3.5 w-3.5 text-slate-600" />
        </div>
      )}
      <div className={cn('min-w-0 max-w-[82%]', isUser && 'flex flex-col items-end')}>
        <div
          className={cn(
            'rounded-lg px-4 py-3 text-sm leading-6',
            isUser
              ? 'bg-slate-900 text-white'
              : 'border border-slate-200 bg-white text-slate-900 shadow-sm',
          )}
        >
          {message.content ? (
            isUser ? (
              <div className="whitespace-pre-wrap">{message.content}</div>
            ) : (
              <>
                <div className="prose prose-slate max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0 prose-headings:mt-3 prose-headings:mb-2 prose-code:before:content-none prose-code:after:content-none">
                  <Markdown remarkPlugins={[remarkGfm]} components={{ code: CodeBlock }}>
                    {message.content}
                  </Markdown>
                </div>
                <WikiImageGrid assets={assets} />
              </>
            )
          ) : (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Thinking</span>
            </div>
          )}
        </div>
        {message.content && (
          <div className={cn('mt-1 flex opacity-0 transition-opacity group-hover:opacity-100', isUser ? 'justify-end' : 'justify-start')}>
            <CopyButton text={message.content} />
          </div>
        )}
      </div>
    </div>
  );
}

function SourcePanel({ sources }: { sources: WikiSource[] }) {
  const metadataValue = (source: WikiSource, key: string) => {
    const value = source.metadata?.[key];
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    return null;
  };

  return (
    <aside className="hidden w-80 shrink-0 flex-col border-l border-slate-200 bg-slate-50 xl:flex">
      <div className="flex h-14 items-center justify-between border-b border-slate-200 px-4">
        <div>
          <div className="text-sm font-semibold text-slate-900">Sources</div>
          <div className="text-xs text-slate-500">{sources.length} ATEK Brain sources</div>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {sources.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-white px-3 py-6 text-center text-sm text-slate-500">
            No sources yet.
          </div>
        ) : (
          <div className="space-y-3">
            {sources.map((source, index) => (
              <div
                key={source.id}
                className="rounded-md border border-slate-200 bg-white p-3 text-left"
              >
                <div className="flex items-start gap-2">
                  <Badge variant="secondary" className="mt-0.5 shrink-0 text-[10px]">
                    {index + 1}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    {source.url || source.githubUrl ? (
                      <a
                        href={source.url || source.githubUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-sm font-medium text-slate-900 transition-colors hover:text-slate-700"
                      >
                        <span className="truncate">{source.title}</span>
                        <ExternalLink className="h-3 w-3 shrink-0 text-slate-400" />
                      </a>
                    ) : (
                      <div className="truncate text-sm font-medium text-slate-900">{source.title}</div>
                    )}
                    <div className="mt-0.5 truncate text-xs text-slate-500">{source.wikiPath}</div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline" className="text-[10px]">
                    {source.sourceLabel || (source.sourceType === 'account_note' ? 'Account note' : 'Wiki')}
                  </Badge>
                  {metadataValue(source, 'ticketId') && (
                    <Badge variant="secondary" className="text-[10px]">
                      {metadataValue(source, 'ticketId')}
                    </Badge>
                  )}
                  {metadataValue(source, 'status') && (
                    <Badge variant="outline" className="text-[10px]">
                      {metadataValue(source, 'status')}
                    </Badge>
                  )}
                  {source.accountName && (
                    <Badge variant="secondary" className="text-[10px]">
                      {source.accountName}
                    </Badge>
                  )}
                </div>
                {source.heading && (
                  <div className="mt-2 text-xs font-medium text-slate-700">{source.heading}</div>
                )}
                <p className="mt-2 line-clamp-4 text-xs leading-5 text-slate-600">{source.snippet}</p>
                <WikiImageGrid assets={source.assets ?? []} compact />
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400">
                  <span>{source.section}</span>
                  {source.authorName && <span>{source.authorName}</span>}
                  {source.lastUpdated && <span>{source.lastUpdated}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}

function formatStatusSummary(status: WikiSyncStatus) {
  const parts = [
    `${status.fileCount} pages`,
    `${status.chunkCount} entries`,
  ];
  if (status.noteCount) parts.push(`${status.noteCount} notes`);
  if (status.qmsClientTicketCount) parts.push(`${status.qmsClientTicketCount} client tickets`);
  return parts.join(', ');
}

export function WikiAssistantPage() {
  const queryClient = useQueryClient();
  const { user } = useSession();
  const canSync = user?.postgres_roles?.includes('full_admin') ?? false;
  const [threads, setThreads] = useState<WikiThread[]>(loadThreads);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(() => threads[0]?.id ?? null);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const { data: status, isFetching: statusLoading } = useQuery({
    queryKey: wikiAssistantKeys.status(),
    queryFn: getWikiSyncStatus,
    staleTime: 60_000,
  });

  const syncMutation = useMutation({
    mutationFn: syncWiki,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wikiAssistantKeys.status() });
      toast.success('Wiki index refreshed');
    },
    onError: (error) => {
      toast.error('Wiki refresh failed', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const activeThread = threads.find((thread) => thread.id === activeThreadId) ?? null;
  const messages = activeThread?.messages ?? [];
  const activeSources = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const sources = messages[index].sources;
      if (sources?.length) return sources;
    }
    return [];
  }, [messages]);
  const threadGroups = useMemo(() => groupThreadsByDate(threads), [threads]);

  useEffect(() => {
    saveThreads(threads);
  }, [threads]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [input]);

  const createThread = useCallback(() => {
    const thread: WikiThread = {
      id: generateId(),
      title: 'New conversation',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    setThreads((prev) => [thread, ...prev]);
    setActiveThreadId(thread.id);
  }, []);

  const deleteThread = useCallback((threadId: string, event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
    setThreads((prev) => prev.filter((thread) => thread.id !== threadId));
    if (activeThreadId === threadId) {
      setActiveThreadId(threads.find((thread) => thread.id !== threadId)?.id ?? null);
    }
  }, [activeThreadId, threads]);

  const updateThreadMessages = useCallback((
    threadId: string,
    updater: (messages: WikiChatMessage[]) => WikiChatMessage[],
  ) => {
    setThreads((prev) => prev.map((thread) => (
      thread.id === threadId
        ? { ...thread, messages: updater(thread.messages), updatedAt: Date.now() }
        : thread
    )));
  }, []);

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const sendMessage = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || isStreaming) return;

    let threadId = activeThreadId;
    const now = Date.now();
    if (!threadId) {
      threadId = generateId();
      const thread: WikiThread = {
        id: threadId,
        title: truncateTitle(text),
        messages: [],
        createdAt: now,
        updatedAt: now,
      };
      setThreads((prev) => [thread, ...prev]);
      setActiveThreadId(threadId);
    }

    const currentMessages = threads.find((thread) => thread.id === threadId)?.messages ?? [];
    const userMessage: WikiChatMessage = {
      id: generateId(),
      role: 'user',
      content: text,
      createdAt: now,
    };
    const assistantMessage: WikiChatMessage = {
      id: generateId(),
      role: 'assistant',
      content: '',
      createdAt: now + 1,
      sources: [],
    };
    const allMessages = [...currentMessages, userMessage];

    setInput('');
    setIsStreaming(true);
    abortRef.current = new AbortController();

    setThreads((prev) => prev.map((thread) => {
      if (thread.id !== threadId) return thread;
      return {
        ...thread,
        title: thread.messages.length === 0 ? truncateTitle(text) : thread.title,
        messages: [...allMessages, assistantMessage],
        updatedAt: Date.now(),
      };
    }));

    const assistantId = assistantMessage.id;
    const tid = threadId;

    try {
      await streamWikiChat(
        {
          messages: allMessages.map((message) => ({
            role: message.role,
            content: message.content,
          })),
        },
        {
          onSources: (sources) => {
            updateThreadMessages(tid, (prev) => prev.map((message) => (
              message.id === assistantId ? { ...message, sources } : message
            )));
          },
          onToken: (token) => {
            updateThreadMessages(tid, (prev) => prev.map((message) => (
              message.id === assistantId ? { ...message, content: `${message.content}${token}` } : message
            )));
          },
          onError: (message) => {
            updateThreadMessages(tid, (prev) => prev.map((entry) => (
              entry.id === assistantId && !entry.content
                ? { ...entry, content: message }
                : entry
            )));
          },
        },
        abortRef.current.signal,
      );
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        updateThreadMessages(tid, (prev) => prev.map((entry) => (
          entry.id === assistantId
            ? {
                ...entry,
                content: entry.content || `Error: ${error instanceof Error ? error.message : 'Failed to get response'}`,
              }
            : entry
        )));
      }
    } finally {
      abortRef.current = null;
      setIsStreaming(false);
    }
  }, [activeThreadId, input, isStreaming, threads, updateThreadMessages]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex -m-5 overflow-hidden bg-white" style={{ height: 'calc(100% + 40px)' }}>
      <div
        className={cn(
          'hidden shrink-0 flex-col overflow-hidden border-r border-slate-200 bg-slate-50 transition-all duration-200 md:flex',
          sidebarOpen ? 'w-72' : 'w-0',
        )}
      >
        <div className="flex h-14 min-w-72 items-center gap-2 border-b border-slate-200 px-3">
          <Button
            variant="outline"
            size="sm"
            className="h-8 flex-1 justify-start gap-2 bg-white"
            onClick={createThread}
          >
            <Plus className="h-3.5 w-3.5" />
            New chat
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSidebarOpen(false)}>
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-w-72 flex-1 overflow-y-auto py-2">
          {threads.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-slate-400">No conversations yet.</div>
          ) : (
            threadGroups.map((group) => (
              <div key={group.label} className="mb-3">
                <div className="px-4 py-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  {group.label}
                </div>
                {group.threads.map((thread) => (
                  <div
                    key={thread.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveThreadId(thread.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') setActiveThreadId(thread.id);
                    }}
                    className={cn(
                      'group mx-2 flex w-[calc(100%-16px)] items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                      activeThreadId === thread.id
                        ? 'border border-slate-200 bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:bg-white hover:text-slate-900',
                    )}
                  >
                    <MessageSquare className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <span className="min-w-0 flex-1 truncate">{thread.title}</span>
                    <button
                      type="button"
                      onClick={(event) => deleteThread(thread.id, event)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') deleteThread(thread.id, event);
                      }}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded opacity-0 transition-opacity hover:bg-red-50 group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      <main className="flex min-w-0 flex-1 flex-col bg-white">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-slate-200 px-4">
          {!sidebarOpen && (
            <Button variant="ghost" size="icon" className="hidden h-8 w-8 md:inline-flex" onClick={() => setSidebarOpen(true)}>
              <PanelLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white">
            <BookOpen className="h-4 w-4 text-slate-700" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-sm font-semibold text-slate-950">ATEK Brain</h1>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              {statusLoading ? (
                <span>Index loading</span>
              ) : status ? (
                <span>{formatStatusSummary(status)}</span>
              ) : (
                <span>Index unavailable</span>
              )}
            </div>
          </div>
          {canSync && (
            <Button
              variant="outline"
              size="sm"
              className="hidden h-8 gap-2 sm:inline-flex"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              <RefreshCw className={cn('h-3.5 w-3.5', syncMutation.isPending && 'animate-spin')} />
              Sync
            </Button>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/60">
          <div className="mx-auto max-w-3xl px-4 py-6">
            {messages.length === 0 ? (
              <div className="flex min-h-[60vh] flex-col items-center justify-center gap-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm">
                  <Sparkles className="h-7 w-7 text-emerald-600" />
                </div>
                <div className="grid w-full max-w-xl grid-cols-1 gap-2 sm:grid-cols-2">
                  {STARTER_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => sendMessage(prompt)}
                      className="rounded-md border border-slate-200 bg-white px-3 py-2.5 text-left text-sm text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-white p-3">
          <div className="mx-auto flex max-w-3xl items-end gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder="Message ATEK Brain"
              disabled={isStreaming}
              className="max-h-44 min-h-11 resize-none rounded-lg border-slate-300 bg-white text-sm"
            />
            {isStreaming ? (
              <Button type="button" size="icon" className="h-11 w-11 shrink-0" onClick={stopStreaming}>
                <Square className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                size="icon"
                className="h-11 w-11 shrink-0"
                disabled={!input.trim()}
                onClick={() => sendMessage()}
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </main>

      <SourcePanel sources={activeSources} />
    </div>
  );
}
